import type { TicketOutput } from "@/api/custom_types";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { useUpdateTicket } from "@/services";
import { getTimeSinceText } from "@/utils/timestamp";
import { Button, Card, Chip, Typography } from "@st-marketplace/ui";
import type { FC } from "react";

/** Open longer than this and a normal ticket is shown as overdue. */
const OVERDUE_MS = 60 * 60 * 1000;

/** "3d 4h", "2h 15m", "12m": how long something has been waiting. */
export function waitingFor(since: string, now = Date.now()) {
	const minutes = Math.max(
		0,
		Math.floor((now - new Date(since).getTime()) / 60_000),
	);
	const days = Math.floor(minutes / 1440);
	const hours = Math.floor((minutes % 1440) / 60);
	if (days) return `${days}d ${hours}h`;
	if (hours) return `${hours}h ${minutes % 60}m`;
	return `${minutes}m`;
}

export function isOverdue(ticket: TicketOutput, now = Date.now()) {
	return (
		ticket.status === "OPEN" &&
		now - new Date(ticket.created_at).getTime() > OVERDUE_MS
	);
}

export const TicketCard: FC<{ ticket: TicketOutput; compact?: boolean }> = ({
	ticket,
	compact,
}) => {
	const { t, lang } = useTranslation();
	const updateTicket = useUpdateTicket();
	const isOpen = ticket.status === "OPEN";
	const urgent = isOpen && ticket.priority === "URGENT";
	const overdue = isOverdue(ticket);

	const setStatus = (status: TicketOutput["status"]) =>
		updateTicket.mutate({ path: { ticket_id: ticket.id }, body: { status } });

	return (
		<Card
			className={cn(
				"flex flex-col gap-3 border-s-4 p-4 sm:flex-row sm:items-start sm:justify-between",
				!isOpen && "border-s-secondary-border opacity-80",
				isOpen && !urgent && !overdue && "border-s-primary-border",
				isOpen && !urgent && overdue && "border-s-warning-border",
				urgent && "border-s-danger-border bg-danger-100/10",
			)}
		>
			<div className="flex min-w-0 flex-1 flex-col gap-2">
				<div className="flex flex-wrap items-center gap-2">
					{urgent ? (
						<Chip variant="error" size="sm">
							{t("admin.tickets.urgent")}
						</Chip>
					) : null}
					{isOpen && overdue ? (
						<Chip variant="warning" size="sm">
							{t("admin.tickets.overdue")}
						</Chip>
					) : null}
					{!isOpen ? (
						<Chip variant="success" size="sm">
							{t("admin.tickets.resolved")}
						</Chip>
					) : null}
					<Typography variant="body-md" as="h3" className="font-semibold">
						{ticket.title}
					</Typography>
				</div>
				{!compact ? (
					<Typography
						variant="body-sm"
						as="p"
						className="whitespace-pre-wrap break-words text-secondary-text-100"
					>
						{ticket.description}
					</Typography>
				) : null}
				<Typography variant="caption" as="p" className="text-secondary-icon">
					{t("admin.tickets.opened", {
						time: getTimeSinceText(ticket.created_at, t, lang),
					})}
					{isOpen
						? ` · ${t("admin.tickets.waiting", {
								time: waitingFor(ticket.created_at),
							})}`
						: null}
					{ticket.closed_at
						? ` · ${t("admin.tickets.closed", {
								time: getTimeSinceText(ticket.closed_at, t, lang),
							})}`
						: null}
					{ticket.reference ? (
						<span className="ms-2 font-mono">{ticket.reference}</span>
					) : null}
				</Typography>
			</div>
			<Button
				type="button"
				size="sm"
				variant={isOpen ? "contained" : "text"}
				color={isOpen ? "primary" : "secondary"}
				loading={updateTicket.isPending}
				onClick={() => setStatus(isOpen ? "CLOSED" : "OPEN")}
				className="shrink-0"
			>
				{isOpen ? t("admin.tickets.resolve") : t("admin.tickets.reopen")}
			</Button>
		</Card>
	);
};
